import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCodelistVersion, replaceCodelistEntries } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { CodelistVersionDetail } from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { formatDateTime } from '../../utils/format';
import { entriesToJson, parseCodelistJson, type ParseResult } from './codelistJson';
import {
  entryStatusColors,
  entryStatusLabelKeys,
  kindLabelKeys,
  versionStatusColors,
  versionStatusLabelKeys,
} from './codelistLabels';

export interface CodelistVersionDialogProps {
  versionGid: string;
  onClose: () => void;
}

/**
 * A version with its entries. A draft's entries can be replaced wholesale as a JSON document — the
 * same shape the import takes, statuses left out because the server recomputes them; anything past
 * draft is history and only read.
 */
export function CodelistVersionDialog({ versionGid, onClose }: CodelistVersionDialogProps) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  // Null while the table is shown; the document being edited otherwise.
  const [json, setJson] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParseResult | null>(null);

  const query = useQuery({
    queryKey: queryKeys.codelists.version(versionGid),
    queryFn: ({ signal }) => getCodelistVersion(versionGid, signal),
  });

  const stopEditing = () => {
    setJson(null);
    setParsed(null);
  };

  const mutation = useMutation({
    mutationFn: () => {
      if (!parsed?.ok) throw new Error('Nothing to save.');
      return replaceCodelistEntries(versionGid, { entries: parsed.value.entries });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.codelists.all });
      toastSuccess(t('codelists.versions.entriesSaved'));
      stopEditing();
    },
    onError: toastError,
  });

  const startEditing = (detail: CodelistVersionDetail) => {
    const text = entriesToJson(detail.entries);
    setJson(text);
    setParsed(parseCodelistJson(text));
  };

  const handleJsonChange = (text: string) => {
    setJson(text);
    setParsed(text.trim() === '' ? null : parseCodelistJson(text));
  };

  const detail = query.data;
  const isDraft = detail?.version.status === 'Draft';
  const editing = json !== null;
  const jsonError =
    parsed !== null && !parsed.ok ? t(parsed.reasonKey, { detail: parsed.detail ?? '' }) : null;
  const canSave = editing && parsed?.ok === true && !mutation.isPending;

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {detail
          ? t('codelists.versions.versionDialog.title', {
              kind: t(kindLabelKeys[detail.version.kind]),
              version: detail.version.versionLabel,
            })
          : t('codelists.versions.actions.detail')}
      </DialogTitle>
      <DialogContent>
        {query.isPending && <LoadingBlock />}
        {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

        {detail && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                color={versionStatusColors[detail.version.status]}
                label={t(versionStatusLabelKeys[detail.version.status])}
              />
              <Typography variant="body2" color="text.secondary">
                {t('codelists.versions.columns.entryCount')}: {detail.version.entryCount}
              </Typography>
              {detail.version.activatedAt && (
                <Typography variant="body2" color="text.secondary">
                  {t('codelists.versions.columns.activatedAt')}: {formatDateTime(detail.version.activatedAt)}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {t('codelists.versions.columns.createdAt')}: {formatDateTime(detail.version.createdAt)}
              </Typography>
            </Stack>

            {detail.version.sourceNote && (
              <Typography variant="body2" color="text.secondary">
                {t('codelists.versions.columns.sourceNote')}: {detail.version.sourceNote}
              </Typography>
            )}

            {!isDraft && <Alert severity="info">{t('codelists.versions.versionDialog.readOnly')}</Alert>}

            {editing ? (
              <TextField
                label={t('codelists.versions.importDialog.json')}
                value={json}
                onChange={(event) => handleJsonChange(event.target.value)}
                multiline
                minRows={12}
                maxRows={24}
                error={jsonError !== null}
                helperText={jsonError ?? t('codelists.versions.versionDialog.jsonHelp')}
                slotProps={{
                  htmlInput: { sx: { fontFamily: 'monospace', fontSize: 13 }, spellCheck: false },
                }}
              />
            ) : (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('codelists.versions.entryColumns.code')}</TableCell>
                      <TableCell>{t('columns.name')}</TableCell>
                      <TableCell>{t('columns.description')}</TableCell>
                      <TableCell>{t('columns.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.entries.map((entry) => (
                      <TableRow key={entry.code} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{entry.code}</TableCell>
                        <TableCell>{entry.name}</TableCell>
                        <TableCell>{entry.description ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant={entry.status === 'Active' ? 'outlined' : 'filled'}
                            color={entryStatusColors[entry.status]}
                            label={t(entryStatusLabelKeys[entry.status])}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }}>
          {detail && isDraft && !editing && (
            <Button onClick={() => startEditing(detail)}>
              {t('codelists.versions.versionDialog.editJson')}
            </Button>
          )}
          {detail && isDraft && editing && (
            <Button onClick={stopEditing} disabled={mutation.isPending}>
              {t('codelists.versions.versionDialog.backToTable')}
            </Button>
          )}
        </Box>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {t('actions.close')}
        </Button>
        {detail && isDraft && editing && (
          <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSave}>
            {t('codelists.versions.versionDialog.saveEntries')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
